import { useMemo, useEffect } from "react";
import { TextInput, SelectInput, required } from "react-admin";
import { useWatch, useFormContext } from "react-hook-form";
import {
  Box,
  Typography,
  Alert,
  Paper,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ContactPageIcon from "@mui/icons-material/ContactPage";
import TableChartIcon from "@mui/icons-material/TableChart";
import TextFieldsIcon from "@mui/icons-material/TextFields";

import { useTemplateContext } from "@/containers/whatsapp-broadcasts/context";
import { extractTemplateVariables, type BroadcastParameter } from "./utils";
import { ITS_COLUMNS } from "./advanced-filter-builder/constants";
import { getVariableSnippet } from "./template-variable-snippet";

type ParamVariable = { number: number; placeholder: string };

const POPULAR_ITS_COLUMNS = new Set([
  "Full_Name",
  "ITS_ID",
  "Mobile",
  "WhatsApp_No",
  "Address",
  "Building",
  "Sector",
  "Sub_Sector",
  "TanzeemFile_No",
  "Email",
  "Age",
]);

const ParameterMapper = () => {
  const templateName = useWatch({ name: "templateName" });
  const parameters =
    (useWatch({ name: "parameters" }) as Record<string, BroadcastParameter> | undefined) || {};
  const csvColumnHeaders = (useWatch({ name: "csvColumnHeaders" }) as string[] | undefined) || [];
  const { setValue, watch } = useFormContext();

  const { data: selectedTemplate, isLoading, error } = useTemplateContext();

  const bodyText = typeof selectedTemplate?.bodyText === "string" ? selectedTemplate.bodyText : "";

  const variableNumbers = useMemo(() => {
    if (!bodyText) return [];
    return extractTemplateVariables(bodyText);
  }, [bodyText]);

  const variables = useMemo(
    (): ParamVariable[] =>
      variableNumbers.map((num) => ({
        number: num,
        placeholder: `{{${num}}}`,
      })),
    [variableNumbers]
  );

  const itsColumnChoices = useMemo(() => {
    const popular: { id: string; name: string }[] = [];
    const others: { id: string; name: string }[] = [];

    ITS_COLUMNS.forEach((col) => {
      const item = { id: col.id, name: col.label };
      if (POPULAR_ITS_COLUMNS.has(col.id)) {
        popular.push(item);
      } else {
        others.push(item);
      }
    });

    return [...popular, ...others];
  }, []);

  const csvColumnChoices = useMemo(
    () =>
      (Array.isArray(csvColumnHeaders) ? csvColumnHeaders : []).map((h) => ({
        id: h,
        name: h,
      })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [JSON.stringify(csvColumnHeaders)]
  );

  useEffect(() => {
    if (variables.length === 0) return;

    const currentParams =
      (watch("parameters") as Record<string, BroadcastParameter> | undefined) || {};
    let needsUpdate = false;
    const updatedParams = { ...currentParams };

    variables.forEach((variable) => {
      const paramKey = String(variable.number);
      const existingParam = currentParams[paramKey];

      if (!existingParam || typeof existingParam !== "object" || !existingParam.type) {
        if (csvColumnChoices.length > 0) {
          const suggestedCol =
            csvColumnChoices.find((c) => {
              const lower = c.name.toLowerCase();
              if (
                variable.number === 1 &&
                (lower.includes("name") || lower.includes("full") || lower.includes("customer"))
              ) {
                return true;
              }
              if (
                variable.number === 2 &&
                (lower.includes("its") || lower.includes("id") || lower.includes("receipt"))
              ) {
                return true;
              }
              return false;
            })?.id || csvColumnChoices[0]?.id;

          updatedParams[paramKey] = {
            type: "column",
            columnSource: "csv",
            column: suggestedCol || "",
          };
        } else {
          updatedParams[paramKey] = {
            type: "column",
            columnSource: "its",
            column: variable.number === 1 ? "Full_Name" : variable.number === 2 ? "ITS_ID" : "",
          };
        }
        needsUpdate = true;
      } else if (
        existingParam.type === "column" &&
        (existingParam as { columnSource?: string }).columnSource === undefined
      ) {
        updatedParams[paramKey] = { ...existingParam, columnSource: "its" };
        needsUpdate = true;
      }
    });

    if (needsUpdate) {
      setValue("parameters", updatedParams, { shouldValidate: true });
    }
  }, [variables, setValue, watch, csvColumnChoices]);

  if (!templateName) {
    return null;
  }

  if (isLoading) {
    return <Typography>Loading template...</Typography>;
  }

  if (error || !selectedTemplate) {
    return (
      <Typography color={error ? "error" : "text.secondary"}>
        {error
          ? `Error loading template: ${(error as Error).message}`
          : `Template not found${templateName ? `: ${templateName}` : ""}`}
      </Typography>
    );
  }

  if (variables.length === 0) {
    return (
      <Alert severity="info" sx={{ borderRadius: 2 }}>
        This template has no variables. No parameter mapping required.
      </Alert>
    );
  }

  return (
    <Box sx={{ width: "100%", maxWidth: "100%", minWidth: 0, boxSizing: "border-box" }}>
      <Box sx={{ mb: 1.5, width: "100%", maxWidth: "100%" }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
          Template Parameters ({variables.length})
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Map each variable placeholder to a directory field, CSV column, or static text.
        </Typography>
      </Box>

      <Stack spacing={1.25} sx={{ width: "100%", maxWidth: "100%", minWidth: 0 }}>
        {variables.map((variable) => {
          const paramKey = String(variable.number);
          const currentParam = (parameters[paramKey] as Record<string, unknown> | undefined) || {};

          const paramType = (currentParam.type as string | undefined) ?? "column";
          const columnSource =
            currentParam.columnSource === "csv" && csvColumnChoices.length > 0 ? "csv" : "its";

          const currentSource: "its" | "csv" | "text" =
            paramType === "text" ? "text" : columnSource;

          const isFilled =
            paramType === "text"
              ? typeof currentParam.value === "string" && currentParam.value.trim().length > 0
              : typeof currentParam.column === "string" &&
                currentParam.column.trim().length > 0 &&
                (columnSource === "csv"
                  ? csvColumnChoices.some((c) => c.id === currentParam.column)
                  : itsColumnChoices.some((c) => c.id === currentParam.column));

          const snippet = getVariableSnippet(bodyText, variable.number);

          const handleSourceChange = (newSource: "its" | "csv" | "text") => {
            const currentParams =
              (watch("parameters") as Record<string, BroadcastParameter> | undefined) || {};
            const existing = (currentParams[paramKey] as Record<string, unknown>) || {};
            let updatedParam: Record<string, unknown>;

            if (newSource === "text") {
              updatedParam = {
                type: "text",
                value: String(existing.value ?? ""),
              };
            } else if (newSource === "csv") {
              const existingCol = String(existing.column ?? "");
              const isValidCsvCol = csvColumnChoices.some((c) => c.id === existingCol);
              updatedParam = {
                type: "column",
                columnSource: "csv",
                column: isValidCsvCol ? existingCol : (csvColumnChoices[0]?.id ?? ""),
              };
            } else {
              const existingCol = String(existing.column ?? "");
              const isValidItsCol = itsColumnChoices.some((c) => c.id === existingCol);
              updatedParam = {
                type: "column",
                columnSource: "its",
                column: isValidItsCol
                  ? existingCol
                  : variable.number === 1
                    ? "Full_Name"
                    : variable.number === 2
                      ? "ITS_ID"
                      : "Full_Name",
              };
            }

            setValue(
              "parameters",
              {
                ...currentParams,
                [paramKey]: updatedParam,
              },
              { shouldValidate: true, shouldDirty: true }
            );
          };

          return (
            <Paper
              key={variable.number}
              variant="outlined"
              sx={{
                p: 1.25,
                borderRadius: 2,
                border: 1,
                borderColor: isFilled ? "divider" : "primary.main",
                bgcolor: "background.paper",
                width: "100%",
                maxWidth: "100%",
                minWidth: 0,
                boxSizing: "border-box",
                transition: "box-shadow 0.2s, border-color 0.2s",
                "&:hover": {
                  boxShadow: 1,
                },
              }}
            >
              {/* Header: Variable badge + Snippet context quote + Status */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 1,
                  mb: 1,
                  width: "100%",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    minWidth: 0,
                    flex: 1,
                  }}
                >
                  <Box
                    sx={{
                      px: 0.8,
                      py: 0.2,
                      borderRadius: 1,
                      bgcolor: "primary.main",
                      color: "primary.contrastText",
                      fontFamily: "monospace",
                      fontWeight: 700,
                      fontSize: "0.78rem",
                      letterSpacing: 0.5,
                      lineHeight: 1.2,
                      flexShrink: 0,
                    }}
                  >
                    {variable.placeholder}
                  </Box>

                  {snippet && (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{
                        fontStyle: "italic",
                        fontSize: "0.78rem",
                        lineHeight: 1.3,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        minWidth: 0,
                      }}
                      title={snippet.fullSnippet}
                    >
                      {snippet.before}
                      <Box
                        component="span"
                        sx={{ color: "text.primary", fontWeight: 700, mx: 0.25 }}
                      >
                        {snippet.placeholder}
                      </Box>
                      {snippet.after}
                    </Typography>
                  )}
                </Box>

                {isFilled ? (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0.5,
                      color: "success.main",
                      flexShrink: 0,
                    }}
                    title="Configured"
                  >
                    <CheckCircleOutlineIcon sx={{ fontSize: "1rem" }} />
                    <Typography
                      variant="caption"
                      sx={{
                        fontWeight: 600,
                        fontSize: "0.72rem",
                        display: { xs: "none", sm: "inline" },
                      }}
                    >
                      Configured
                    </Typography>
                  </Box>
                ) : (
                  <Typography
                    variant="caption"
                    sx={{
                      color: "error.main",
                      fontWeight: 600,
                      fontSize: "0.72rem",
                      flexShrink: 0,
                    }}
                  >
                    Required
                  </Typography>
                )}
              </Box>

              {/* Source Mode Selector & Value Input side-by-side in one compact row */}
              <Stack
                direction="row"
                spacing={1}
                alignItems="flex-start"
                sx={{ width: "100%", minWidth: 0 }}
              >
                <FormControl
                  size="small"
                  sx={{
                    width: { xs: 110, sm: 130 },
                    flexShrink: 0,
                    m: 0,
                  }}
                >
                  <InputLabel id={`source-label-${paramKey}`} sx={{ fontSize: "0.8rem" }}>
                    Source
                  </InputLabel>
                  <Select
                    labelId={`source-label-${paramKey}`}
                    value={currentSource}
                    label="Source"
                    onChange={(e) => handleSourceChange(e.target.value as "its" | "csv" | "text")}
                    sx={{
                      fontSize: "0.8125rem",
                      borderRadius: 1.5,
                      "& .MuiSelect-select": {
                        py: 0.75,
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                      },
                    }}
                  >
                    <MenuItem value="its" sx={{ fontSize: "0.8125rem", py: 0.75 }}>
                      <ContactPageIcon
                        sx={{ fontSize: "0.95rem", mr: 0.75, color: "text.secondary" }}
                      />
                      Directory
                    </MenuItem>
                    {csvColumnChoices.length > 0 && (
                      <MenuItem value="csv" sx={{ fontSize: "0.8125rem", py: 0.75 }}>
                        <TableChartIcon
                          sx={{ fontSize: "0.95rem", mr: 0.75, color: "text.secondary" }}
                        />
                        CSV
                      </MenuItem>
                    )}
                    <MenuItem value="text" sx={{ fontSize: "0.8125rem", py: 0.75 }}>
                      <TextFieldsIcon
                        sx={{ fontSize: "0.95rem", mr: 0.75, color: "text.secondary" }}
                      />
                      Text
                    </MenuItem>
                  </Select>
                </FormControl>

                <Box
                  sx={{
                    flex: 1,
                    minWidth: 0,
                    "& .MuiFormControl-root": {
                      width: "100%",
                      maxWidth: "100%",
                      minWidth: 0,
                      m: 0,
                    },
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 1.5,
                      fontSize: "0.8125rem",
                    },
                  }}
                >
                  {currentSource === "text" && (
                    <TextInput
                      source={`parameters.${paramKey}.value`}
                      label="Static text"
                      placeholder="Enter static text..."
                      validate={required()}
                      fullWidth
                      size="small"
                      helperText={false}
                      sx={{ width: "100%", maxWidth: "100%", m: 0 }}
                    />
                  )}
                  {currentSource === "its" && (
                    <SelectInput
                      source={`parameters.${paramKey}.column`}
                      label="Directory field"
                      choices={itsColumnChoices}
                      validate={required()}
                      fullWidth
                      size="small"
                      helperText={false}
                      sx={{ width: "100%", maxWidth: "100%", m: 0 }}
                    />
                  )}
                  {currentSource === "csv" && (
                    <SelectInput
                      source={`parameters.${paramKey}.column`}
                      label="CSV column"
                      choices={csvColumnChoices}
                      validate={required()}
                      fullWidth
                      size="small"
                      helperText={false}
                      sx={{ width: "100%", maxWidth: "100%", m: 0 }}
                    />
                  )}
                </Box>
              </Stack>
            </Paper>
          );
        })}
      </Stack>
    </Box>
  );
};

export default ParameterMapper;
